import { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { NumberField } from "@/components/DecimalInput";
import { WEIGHT_OPTS, parseDecimal } from "@/lib/decimal";
import { PaketInhalt, initialInhalt, inhaltBody } from "@/components/PaketInhalt";
import { isPart, openPdf } from "@/lib/produktion";
import { openAuthedFile, labelFilename } from "@/lib/openFile";
import { OhneLabelOption, OHNE_LABEL_ROLES, initialOhneLabel, isAbholung } from "@/components/OhneLabelOption";
import { FileDown, Printer, CheckCircle2 } from "lucide-react";
import { LabelFormatSelect } from "@/components/LabelFormatSelect";
import { VersandmitteilungButton } from "@/components/VersandmitteilungButton";
import { DEFAULT_LABEL_FORMAT, useVersandSettings } from "@/lib/labelFormat";
import { VersandKundeWahl } from "@/components/VersandKundeWahl";

// Nach dem Erstellen eines Pakets: Ergebnis + Packzettel/Label drucken
const CreatedView = ({ s, onClose }) => (
  <div className="space-y-3" data-testid="versand-created">
    <div className="flex items-center gap-2 text-emerald-700 font-medium"><CheckCircle2 className="w-5 h-5" /> Paket angelegt{s.tracking_number && ` · ${s.tracking_number}`}</div>
    <ul className="text-sm text-slate-700 list-disc pl-5">{s.inhalt.map((i, n) => <li key={`${i.position_id}-${n}`}>{i.menge} × {i.bezeichnung}{i.zusaetzlich && <span className="text-amber-700"> (zusätzlich)</span>}</li>)}
      {s.kundenteile_beigelegt.map((t) => <li key={t.id}>Kundenteil: {t.bezeichnung}</li>)}
      {s.inhalt_text && <li className="whitespace-pre-line" data-testid="versand-created-inhalt-text">Inhalt / Grund: {s.inhalt_text}</li>}</ul>
    <p className="text-sm" data-testid="versand-created-status">{s.zusatzpaket ? "Zusätzliches Paket gebucht – der Auftrag bleibt vollständig versendet." : s.letztes_paket ? "Auftrag vollständig versendet." : `Teillieferung – noch offen: ${s.rest.reduce((n, r) => n + r.menge, 0)} Stück.`}</p>
    <div className="flex gap-2 flex-wrap">
      <Button onClick={() => openPdf(`/versand/${s.id}/packzettel.pdf`, `Packzettel_${s.auftragsnummer || s.id}.pdf`).catch((e) => toast.error(`Packzettel konnte nicht erzeugt werden. ${e.message}`))} data-testid="versand-created-packzettel"><Printer className="w-4 h-4 mr-1" /> Packzettel drucken</Button>
      {s.label_url && <Button variant="outline" onClick={() => openAuthedFile(s.label_url, labelFilename(s)).catch((e) => toast.error(`Label konnte nicht geöffnet werden: ${e.message}`))} data-testid="versand-created-label"><FileDown className="w-4 h-4 mr-1" /> Label</Button>}
      {/* letzter Schritt: Versandmitteilung (inkl. Rechnung) – läuft im Hintergrund, „Fertig“ schließt nur */}
      <VersandmitteilungButton s={s} size="default" />
      <Button variant="ghost" className="ml-auto" onClick={onClose} data-testid="versand-created-close">Fertig</Button>
    </div>
  </div>
);

const MANUAL = "__manuell__";
const EMPTY_EMPF = { firma: "", vorname: "", nachname: "", strasse: "", adresszusatz: "", plz: "", ort: "", land: "DE", email: "", telefon: "" };
const REQUIRED = [["vorname", "Vorname"], ["nachname", "Nachname"], ["strasse", "Straße + Hausnr."], ["plz", "PLZ"], ["ort", "Ort"]];

const EmpfaengerForm = ({ value, onChange, children }) => {
  const f = (key, label, req, cls = "") => (
    <div className={cls}>
      <Label htmlFor={`versand-empf-${key}`}>{label}{req && <span className="text-rose-600"> *</span>}</Label>
      <Input id={`versand-empf-${key}`} value={value[key]} onChange={(e) => onChange({ ...value, [key]: e.target.value })} data-testid={`versand-empf-${key}`} />
    </div>
  );
  return (
    <div className="grid grid-cols-2 gap-3 p-3 border rounded-md bg-slate-50" data-testid="versand-empfaenger-form">
      <div className="col-span-2 text-xs uppercase tracking-wider text-slate-500">Empfänger</div>
      {children && <div className="col-span-2">{children}</div>}
      {f("firma", "Firma", false, "col-span-2")}
      {f("vorname", "Vorname", true)}{f("nachname", "Nachname", true)}
      {f("strasse", "Straße + Hausnr.", true, "col-span-2")}
      {f("adresszusatz", "Adresszusatz", false, "col-span-2")}
      {f("plz", "PLZ", true)}{f("ort", "Ort", true)}
      {f("land", "Land (ISO, z. B. DE)", false)}{f("telefon", "Telefon", false)}
      {f("email", "E-Mail (für Tracking)", false, "col-span-2")}
    </div>
  );
};

export const ShipmentDialog = ({ open, onOpenChange, orders = [], fixedOrder = null, onCreated, folgepaket = false }) => {
  const [inhalt, setInhalt] = useState(() => initialInhalt(fixedOrder));
  const [ol, setOl] = useState({ on: false, dienst: "", tracking: "" }); // „Ohne DHL-Label buchen“
  const canOhneLabel = !!fixedOrder && OHNE_LABEL_ROLES.includes(getUser()?.rolle);
  const [created, setCreated] = useState(null); // nach dem Erstellen: Packzettel/Label direkt drucken
  const [form, setForm] = useState({ gewicht_kg: "1", groesse: "M", versandart: "paket" });
  const [orderId, setOrderId] = useState(MANUAL);
  const [empf, setEmpf] = useState(EMPTY_EMPF);
  const [kundeSel, setKundeSel] = useState(null); // manueller Versand: gewählter Bestandskunde (kunde, quelle, adresse_speichern)
  const [saving, setSaving] = useState(false);
  const settings = useVersandSettings(open);
  const [labelFormat, setLabelFormat] = useState(null); // null = Standard aus Einstellungen → Versand
  const lf = labelFormat || settings?.label_format || DEFAULT_LABEL_FORMAT;

  useEffect(() => {
    if (open) {
      const sp = fixedOrder?.positionen?.find((p) => p.typ === "versand");
      const pre = ["paket", "kleinpaket"].includes(sp?.versand_label_typ) ? sp.versand_label_typ : "paket";
      setForm({ gewicht_kg: "1", groesse: "M", versandart: pre }); setOrderId(fixedOrder?.id || MANUAL); setEmpf(EMPTY_EMPF); setKundeSel(null);
      setInhalt(initialInhalt(fixedOrder)); setCreated(null); setLabelFormat(null);
      setOl(fixedOrder && OHNE_LABEL_ROLES.includes(getUser()?.rolle) ? initialOhneLabel(fixedOrder) : { on: false, dienst: "", tracking: "" });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- nur beim Öffnen zurücksetzen (Neuladen des Auftrags nach dem Erstellen darf die Erfolgsansicht nicht schließen)

  const manual = orderId === MANUAL;
  const weight = parseDecimal(form.gewicht_kg, WEIGHT_OPTS);
  const tooHeavy = form.versandart === "kleinpaket" && !weight.error && weight.value > 1;
  const orderList = fixedOrder ? [fixedOrder] : orders;

  const submit = async () => {
    if (manual) {
      const missing = REQUIRED.filter(([k]) => !empf[k].trim()).map(([, l]) => l);
      if (missing.length) { toast.error(`Pflichtfelder fehlen: ${missing.join(", ")}`); return; }
    }
    if (!ol.on && weight.error) { toast.error(`Gewicht: ${weight.error}`); return; }
    if (!ol.on && tooHeavy) { toast.error("Gewicht überschreitet 1 kg — bitte DHL Paket wählen."); return; }
    const withInhalt = fixedOrder && (fixedOrder.positionen || []).some(isPart);
    const ib = withInhalt ? inhaltBody(fixedOrder, inhalt) : { body: {} };
    if (ib.error) { toast.error(ib.error); return; }
    setSaving(true);
    try {
      const olBody = ol.on ? { ohne_label: true, versanddienst: ol.dienst.trim() || null, tracking_manuell: isAbholung(ol) ? null : ol.tracking.trim() || null } : {};
      const body = { ...form, gewicht_kg: weight.value ?? 1, order_id: manual ? null : orderId, empfaenger: manual ? empf : null, ...ib.body, ...olBody,
        ...(manual && kundeSel ? { kunde_id: kundeSel.kunde.id, adresse_speichern: !kundeSel.quelle && !!kundeSel.adresse_speichern } : {}),
        ...(ol.on ? {} : { label_format: lf }) };
      const r = await api.post("/versand", body);
      if (r.data.error) toast.error(`Label-Fehler: ${r.data.error}`);
      else toast.success(ol.on ? "Versand ohne DHL-Label gebucht." : r.data.tracking_number ? `Label erstellt · ${r.data.tracking_number}` : "Versand angelegt.");
      if (r.data.kunde_adresse_gespeichert) toast.success(`Adresse bei ${r.data.kunde_name} gespeichert.`);
      onCreated?.(r.data);
      if (r.data.gebucht) setCreated(r.data); else onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="versand-dialog">
        <DialogHeader><DialogTitle>{fixedOrder ? `Versand für ${fixedOrder.auftragsnummer} erzeugen` : "Neuen Versand anlegen"}</DialogTitle></DialogHeader>
        {created ? <CreatedView s={created} onClose={() => onOpenChange(false)} /> : <>
        <div className="space-y-3">
          <div><Label>Auftrag</Label>
            <Select value={orderId} onValueChange={setOrderId} disabled={!!fixedOrder}>
              <SelectTrigger data-testid="versand-order-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {!fixedOrder && <SelectItem value={MANUAL} data-testid="versand-order-manual">Manueller Versand ohne Auftrag</SelectItem>}
                {orderList.map((o) => <SelectItem key={o.id} value={o.id}>{o.auftragsnummer} — {o.titel}</SelectItem>)}
              </SelectContent>
            </Select>
            {!manual && <p className="text-xs text-slate-500 mt-1">Empfänger wird aus der Liefer- bzw. Rechnungsadresse des Kunden übernommen.</p>}
            {manual && !fixedOrder && <p className="text-xs text-slate-500 mt-1" data-testid="versand-manuell-hint">Versand ohne Auftrag (manuelles Label). Für eine Sendung zu einem Auftrag oben den Auftrag wählen.</p>}
          </div>
          {manual && (
            <EmpfaengerForm value={empf} onChange={setEmpf}>
              <VersandKundeWahl sel={kundeSel} onEmpfaenger={setEmpf} onChange={(v, clear) => { setKundeSel(v); if (clear) setEmpf(EMPTY_EMPF); }} />
            </EmpfaengerForm>
          )}
          {canOhneLabel && <OhneLabelOption value={ol} onChange={setOl} />}
          {!ol.on && <>
          <div><Label>Versandart <span className="text-rose-600">*</span></Label>
            <Select value={form.versandart} onValueChange={(v) => setForm({ ...form, versandart: v })}>
              <SelectTrigger data-testid="versand-versandart-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paket">DHL Paket (bis 31,5 kg)</SelectItem>
                <SelectItem value="kleinpaket">DHL Kleinpaket bis 1 kg (Warenpost)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className={`grid gap-3 ${form.versandart === "paket" ? "grid-cols-2" : "grid-cols-1"}`}>
            <div><Label htmlFor="versand-gewicht">Gewicht (kg)</Label><NumberField id="versand-gewicht" align="left" opts={WEIGHT_OPTS} value={form.gewicht_kg} onChange={(t) => setForm((x) => ({ ...x, gewicht_kg: t }))}
              placeholder="z. B. 0,8" aria-label="Gewicht in kg" testId="versand-gewicht"
              hint={<p className="text-[11px] text-slate-500 mt-0.5">{form.versandart === "kleinpaket" ? "Kleinpaket: max. 1 kg, z. B. 0,8" : "max. 31,5 kg"}</p>} /></div>
            {form.versandart === "paket" && (
              <div><Label>Größe</Label>
                <Select value={form.groesse} onValueChange={(v) => setForm({ ...form, groesse: v })}>
                  <SelectTrigger data-testid="versand-groesse"><SelectValue /></SelectTrigger>
                  <SelectContent>{["S", "M", "L", "XL"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <LabelFormatSelect value={lf} onChange={setLabelFormat} testId="versand-label-format" />
          {tooHeavy && <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-sm" data-testid="versand-weight-warning">⚠ Gewicht überschreitet 1 kg — bitte „DHL Paket" wählen.</div>}
          </>}
          {fixedOrder && (fixedOrder.positionen || []).some(isPart) && <PaketInhalt order={fixedOrder} folgepaket={folgepaket} value={inhalt} onChange={setInhalt} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={saving || (!ol.on && (tooHeavy || !!weight.error))} data-testid="versand-save">{ol.on ? "Versand buchen" : "Label erzeugen"}</Button>
        </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
};
